import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, CornerDownRight } from 'lucide-react';
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from '@shared/ui/sidebar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@shared/ui/collapsible';
import { Badge } from '@shared/ui/badge';
import { SuiteIcon } from '@shared/icons';
import { SuiteNavItem } from '@suites/manifest';
import { cn } from '@shared/lib/utils';

export interface SidebarNavItemProps {
  item: SuiteNavItem;
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({ item }) => {
  const { isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const currentFull = `${location.pathname}${location.search || ''}`;

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const hasChildren = Boolean(item.children && item.children.length > 0);

  const isChildActive = (childPath: string) => {
    if (childPath.includes('?')) {
      return currentFull === childPath;
    }
    return location.pathname === childPath && !location.search;
  };

  const isAnyChildActive = hasChildren && item.children!.some((c) => isChildActive(c.path));
  const isDirectActive = hasChildren
    ? location.pathname === item.path && !location.search && !isAnyChildActive
    : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (isAnyChildActive || location.pathname === item.path) {
      setIsOpen(true);
    }
  }, [isAnyChildActive, location.pathname, item.path]);

  if (!hasChildren) {
    return (
      <SidebarMenuItem key={item.path}>
        <SidebarMenuButton
          asChild
          isActive={isDirectActive}
          tooltip={item.label}
          className="gap-2.5 px-3 font-medium text-xs text-sidebar-foreground touch:min-h-11"
        >
          <NavLink to={item.path} onClick={handleNavClick}>
            <SuiteIcon name={item.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate flex-1">{item.label}</span>
            {item.status === 'planned' && (
              <Badge
                variant="secondary"
                className="ml-auto text-[9px] px-1 py-0 font-normal uppercase tracking-wider text-muted-foreground"
              >
                Soon
              </Badge>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <SidebarMenuItem key={item.path}>
        <div className="flex items-center w-full">
          <SidebarMenuButton
            asChild
            isActive={isDirectActive}
            tooltip={item.label}
            className="gap-2.5 px-3 font-medium text-xs text-sidebar-foreground flex-1 touch:min-h-11"
          >
            <NavLink to={item.path} onClick={handleNavClick} className="flex items-center gap-2 w-full">
              <SuiteIcon name={item.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate flex-1">{item.label}</span>
              {item.children!.length > 1 && (
                <span className="rounded bg-muted px-1.5 py-0.2 text-[10px] font-mono text-muted-foreground font-medium">
                  {item.children!.length - 1}
                </span>
              )}
            </NavLink>
          </SidebarMenuButton>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/50 transition-colors mr-1 cursor-pointer touch:min-h-11 touch:min-w-11"
              aria-label={`Toggle ${item.label} subitems`}
            >
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform duration-200 text-muted-foreground',
                  isOpen ? 'rotate-0' : '-rotate-90'
                )}
              />
            </button>
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
          <SidebarMenuSub className="py-1">
            {item.children!.map((child) => {
              const active = isChildActive(child.path);
              return (
                <SidebarMenuSubItem key={child.path}>
                  <SidebarMenuSubButton
                    asChild
                    size="sm"
                    isActive={active}
                    className="font-medium text-xs text-sidebar-foreground h-7 touch:min-h-11"
                  >
                    <NavLink to={child.path} onClick={handleNavClick} className="flex items-center gap-1.5 w-full">
                      <CornerDownRight
                        className={cn(
                          'h-3 w-3 shrink-0 transition-colors',
                          active ? 'text-primary font-bold' : 'text-muted-foreground/60'
                        )}
                      />
                      <SuiteIcon
                        name={child.icon || 'box'}
                        className={cn(
                          'h-3.5 w-3.5 shrink-0',
                          active ? 'text-primary' : 'text-muted-foreground'
                        )}
                      />
                      <span className="truncate">{child.label}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
